import {
  type Position, type BFSNode, type PredationResult,
  Animal, Terrain, Direction,
} from "./types"
import { Grid } from "./grid"
import { posKey } from "./island"
import {
  PREY_MAP, PREDATOR_RANGE,
  FOREST_PROTECTION_MIN_DISTANCE, MOUNTAIN_CONSECUTIVE_BLOCK,
} from "./constants"

/**
 * Find all prey reachable by a predator using BFS with mountain-blocking.
 * Returns positions of reachable prey (before forest protection check).
 */
export function findReachablePrey(
  grid: Grid,
  predatorPos: Position,
  range: number,
  island: Set<string>,
  preyTypes: Animal[],
): { pos: Position; animal: Animal }[] {
  const predatorTile = grid.get(predatorPos)
  if (!predatorTile) return []

  const startConsec = predatorTile.terrain === Terrain.Mountain ? 1 : 0

  // visited: posKey → minimum consecutiveMountains seen at that position
  const visited = new Map<string, number>()
  visited.set(posKey(predatorPos), startConsec)

  const queue: BFSNode[] = [{
    pos: predatorPos,
    distance: 0,
    consecutiveMountains: startConsec,
  }]

  const reachable: { pos: Position; animal: Animal }[] = []

  while (queue.length > 0) {
    const node = queue.shift()!
    const { pos, distance, consecutiveMountains } = node

    // Check for prey at current position (not at distance 0 = self)
    if (distance > 0) {
      const tile = grid.get(pos)
      if (tile && tile.animal && preyTypes.includes(tile.animal)) {
        // Forest protection: prey on forest at distance >= 2 survives
        const isProtected = tile.terrain === Terrain.Forest &&
          distance >= FOREST_PROTECTION_MIN_DISTANCE
        if (!isProtected) {
          reachable.push({ pos, animal: tile.animal })
        }
      }
    }

    // Don't expand beyond range
    if (distance >= range) continue

    // Explore LAND-connected neighbors on the same island
    for (const dir of [Direction.N, Direction.E, Direction.S, Direction.W]) {
      const neighbor = grid.getNeighbor(pos, dir)
      const nKey = posKey(neighbor)

      // Must be on the same island and LAND-connected
      if (!island.has(nKey)) continue
      if (!grid.areLandConnected(pos, neighbor)) continue

      const neighborTile = grid.get(neighbor)
      if (!neighborTile) continue

      // Calculate consecutive mountains
      const newConsec = neighborTile.terrain === Terrain.Mountain
        ? consecutiveMountains + 1
        : 0

      // Mountain wall: 2 consecutive mountains block propagation
      if (newConsec >= MOUNTAIN_CONSECUTIVE_BLOCK) continue

      // Only re-explore if we found a path with fewer consecutive mountains
      const prevConsec = visited.get(nKey)
      if (prevConsec !== undefined && prevConsec <= newConsec) continue

      visited.set(nKey, newConsec)
      queue.push({
        pos: neighbor,
        distance: distance + 1,
        consecutiveMountains: newConsec,
      })
    }
  }

  return reachable
}

/**
 * Resolve predation on an island.
 * Resolution order: bears first (can kill wolves), then wolves.
 * Returns kills and herbivore death count.
 */
export function resolvePredation(
  grid: Grid,
  island: Position[],
  _dryRun: boolean = false,
): PredationResult {
  const islandSet = new Set(island.map(posKey))
  const kills: { pos: Position; animal: Animal }[] = []
  let herbivoreDeaths = 0

  // Phase 1: Bears
  const bears: Position[] = []
  for (const pos of island) {
    const tile = grid.get(pos)
    if (tile?.animal === Animal.Bear) bears.push(pos)
  }

  for (const bearPos of bears) {
    const range = PREDATOR_RANGE[Animal.Bear]!
    const prey = findReachablePrey(grid, bearPos, range, islandSet, PREY_MAP[Animal.Bear])
    for (const { pos, animal } of prey) {
      // Verify prey still exists (may have been killed by another bear)
      const tile = grid.get(pos)
      if (!tile || tile.animal !== animal) continue

      kills.push({ pos, animal })
      if (animal === Animal.Sheep || animal === Animal.Deer) {
        herbivoreDeaths++
      }
      if (!_dryRun) {
        tile.removeAnimal()
      }
    }
  }

  // Phase 2: Wolves
  const wolves: Position[] = []
  for (const pos of island) {
    const tile = grid.get(pos)
    if (tile?.animal === Animal.Wolf) wolves.push(pos)
  }

  for (const wolfPos of wolves) {
    const range = PREDATOR_RANGE[Animal.Wolf]!
    const prey = findReachablePrey(grid, wolfPos, range, islandSet, PREY_MAP[Animal.Wolf])
    for (const { pos, animal } of prey) {
      const tile = grid.get(pos)
      if (!tile || tile.animal !== animal) continue

      kills.push({ pos, animal })
      if (animal === Animal.Sheep || animal === Animal.Deer) {
        herbivoreDeaths++
      }
      if (!_dryRun) {
        tile.removeAnimal()
      }
    }
  }

  return { kills, herbivoreDeaths }
}
